/**
 * Product Reviews Summary
 *
 * Small custom element that renders Yotpo Storefront v3 bottomline data
 * (average score + total review count) as filled dots plus an underlined
 * count link. Clicking the link opens the reviews modal (default
 * `#ProductReviewsModal`) and pushes a `review_summary_click` dataLayer event.
 *
 * Multiple instances on the same page share a single fetch via a module-level
 * promise cache keyed by productId.
 *
 * Re-fetches on the bubbling `product:change-requested` event so it updates
 * when a Combined Listing sibling is selected.
 */

(() => {
  if (customElements.get('product-reviews-summary')) return;

  const API_HOST = 'https://api-cdn.yotpo.com';

  // productId (string) -> Promise<bottomline | null>
  const bottomlineCache = new Map();

  function fetchBottomline(appKey, productId) {
    const key = String(productId);
    if (bottomlineCache.has(key)) return bottomlineCache.get(key);

    const url = `${API_HOST}/v3/storefront/store/${encodeURIComponent(appKey)}/product/${encodeURIComponent(productId)}/reviews?perPage=1&page=1`;
    const promise = fetch(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Yotpo-Token': appKey,
      },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        // v3 storefront is flat; v1 wraps in `response`. Handle both.
        const root = data?.bottomline ? data : data?.response || data || {};
        return root.bottomline || null;
      })
      .catch((err) => {
        console.warn('[product-reviews-summary] fetch failed', err);
        return null;
      });

    bottomlineCache.set(key, promise);
    return promise;
  }

  const HTML_ESCAPE = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };

  const escapeHTML = (str) =>
    String(str == null ? '' : str).replace(/[&<>"']/g, (m) => HTML_ESCAPE[m]);

  const dotsHTML = (score) => {
    const out = [];
    for (let i = 1; i <= 5; i += 1) {
      let pct = 0;
      if (score >= i) pct = 100;
      else if (score > i - 1) pct = Math.round((score - (i - 1)) * 100);
      out.push(`<span class="product-reviews-summary__dot" style="--fill:${pct}%"></span>`);
    }
    return out.join('');
  };

  class ProductReviewsSummary extends HTMLElement {
    constructor() {
      super();
      this.appKey = this.dataset.yotpoAppKey || '';
      this.productId = this.dataset.productId || '';
      this.productHandle = this.dataset.productHandle || '';
      this.location = this.dataset.location || '';
      this.modalId = this.dataset.modalId || 'ProductReviewsModal';
      this.locale = this.dataset.pageLocale || document.documentElement.lang || 'en';
      this.boundProductChange = null;
      this.requestId = 0;
    }

    connectedCallback() {
      this.render();
      this.boundProductChange = this.handleProductChange.bind(this);
      document.addEventListener('product:change-requested', this.boundProductChange);
    }

    disconnectedCallback() {
      if (this.boundProductChange) {
        document.removeEventListener('product:change-requested', this.boundProductChange);
        this.boundProductChange = null;
      }
    }

    handleProductChange(event) {
      const newId = String(event?.detail?.productId || '');
      if (!newId || newId === String(this.productId)) return;
      this.productId = newId;
      this.dataset.productId = newId;
      this.render();
    }

    async render() {
      if (!this.appKey || !this.productId) {
        this.hidden = true;
        return;
      }

      const requestId = ++this.requestId;
      this.hidden = true;

      const bottomline = await fetchBottomline(this.appKey, this.productId);
      if (requestId !== this.requestId) return; // sibling switched mid-fetch
      if (!bottomline || !(bottomline.totalReview > 0)) return;

      const score = parseFloat(bottomline.averageScore || 0) || 0;
      const total = parseInt(bottomline.totalReview || 0, 10) || 0;
      const formattedTotal = total.toLocaleString(this.locale);

      this.innerHTML = `
        <button
          type="button"
          class="product-reviews-summary__button"
          data-open-modal="#${escapeHTML(this.modalId)}"
          aria-haspopup="dialog"
        >
          <span class="product-reviews-summary__dots" aria-hidden="true">${dotsHTML(score)}</span>
          <span class="product-reviews-summary__count">${escapeHTML(formattedTotal)}</span>
          <span class="sr-only">${escapeHTML(formattedTotal)} customer ratings — open reviews</span>
        </button>
      `;
      this.hidden = false;

      const button = this.querySelector('button');
      if (button) {
        button.addEventListener('click', () => {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'review_summary_click',
            link_location: this.location || 'unknown',
            product_id: this.productId,
            product_handle: this.productHandle,
          });
        });
      }
    }
  }

  customElements.define('product-reviews-summary', ProductReviewsSummary);
})();
